/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

  // js/common/utilities/format-money.js
function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ",");
    decimal = defaultTo(decimal, ".");
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
    return dollarsAmount + centsAmount;
  }
  let value = "";
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
    default:
      value = formatWithDelimiters(cents, 2);
      break;
  }
  if (formatString.indexOf("with_comma_separator") !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}
  var currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;

$(document).ready(function() {

  $('body').on('change','#OutletLocality', function(){
    var selectedLocality = $(this).val();
     // Show all items if "All" is selected
      if (selectedLocality === 'all') {
        $('.stor-locator-result-grid-item').show();
      } else {
        // Hide all and show only the matching locality
        $('.stor-locator-result-grid-item').hide();
        $(`.stor-locator-result-grid-item[data-serachval='${selectedLocality}']`).show();
      }
      var visibleItemCount = $('.stor-locator-result-grid-item:visible').length;
      // Set the count number to the variable and update the DOM element
      var countStore = visibleItemCount;
      $('.store-locator-count-number').html(countStore);
    
  });

  var width = $(window).outerWidth();
  if(width<=999){
    $('body').on('click','.footer__item--links .footer__item-title', function(){
      $(this).toggleClass('active');
      $(this).next('.footer__item-content').slideToggle();
    })
  }

  $('body').on('click','.product-meta-collapsible__item-question', function(){
    var activeTitle = $(this);
    activeTitle.toggleClass('active');    
    var currentDropdown = activeTitle.next('.product-meta-collapsible__item-content');
    $('.product-meta-collapsible__item-question').not(activeTitle).removeClass('active');
    $('.product-meta-collapsible__item-content').not(currentDropdown).slideUp();
    currentDropdown.stop(true, true).slideToggle();
  });

  $('body').on('click','.faq-item-collapsible', function(){
    var activeTitlee = $(this);
    activeTitlee.toggleClass('active');    
    var currentDropdownn = activeTitlee.next('.faq-item-collapsible-content');
    $('.faq-item-collapsible').not(activeTitlee).removeClass('active');
    $('.faq-item-collapsible-content').not(currentDropdownn).slideUp();
    currentDropdownn.stop(true, true).slideToggle();
  });
    

  // CUSTOM VARIANT ============== 

  const numberr = $(".product-meta__minorder").data("miniorder"); 
  const items = $(".cvov-value-list");
  const subtotalElement = $("#variant-subtotal");
  // var subtotal = 0;
  function updateSubtotal() {
    let subtotal = 0;
    items.each(function () {
        const item = $(this);
        const quantity = parseFloat(item.find(".cvov-value-quantity-selector__input").val()) || 0;
        const price = parseFloat(item.find(".cvov-value-price-item").data("price"));
        subtotal += quantity * price;
    });
    console.log('subtotal',subtotal);
    console.log('numberr',numberr);
    subtotalElement.html(formatMoney(subtotal, currencyFormat));
    if(numberr > subtotal) {
      $('.custom-variant-atc-button').hide();
      $('.custom-variant-atc-button-popup').show();
      $('.custom-variant-error-message').show();
    }else {
      $('.custom-variant-atc-button').show();
      $('.custom-variant-atc-button-popup').hide();
      $('.custom-variant-error-message').hide();
    }
    if(numberr == subtotal) {
      $('.custom-variant-atc-button').show();
    }
  }
  items.each(function () {
    const item = $(this);
    const quantity  = item.find(".cvov-value-quantity-selector__input");
    function checkAllZero() {
        const allZero = $(".cvov-value-quantity-selector__input").toArray().every(input => parseFloat($(input).val()) === 0);
        $(".custom-variant-atc-button").prop("disabled", allZero);
    }
    item.find(".button-plus").on("click", function () {
        quantity.val(parseFloat(quantity.val()) + 1 || 1);
        updateSubtotal();
        checkAllZero();
    });
    item.find(".button-minus").on("click", function () {
       quantity.val(Math.max(0, parseFloat(quantity.val()) - 1 || 0));
       updateSubtotal();
        checkAllZero();
    });
    item.find(".cvov-value-quantity-selector__input").on("input", updateSubtotal);
    checkAllZero();
  });
  
  $('body').on('click','.custom-variant-atc-button', function(){
    if ($(this).hasClass("disabled")){
      return;
    }
    var _this = $(this);
    _this.attr("disabled", "disabled");
    _this.attr("aria-busy", "true");
    var items = [];
    var randomId = Math.random().toString(36).substring(2, 12);
    _this.closest('.custom-variant-wrap').find('.cvov-value-list').each(function(i,v){
      var val = $(v).find('.cvov-value-item').attr('data-vid');  
      var qnt = $(v).find('.cvov-value-quantity-selector__input').val();
      items.push({
        quantity: qnt,
        id: val,
        properties: {
            '_personalize': 'sub',
            'minimum_order': numberr,
            '_bundleid': randomId
          }
      });
    });
    var data = {items};
    $.ajax({
        type: 'POST',
        url: '/cart/add.js',
        async: false,
        data: {items},
        dataType: 'json',
        error: function(jqXHR, textStatus, errorThrown){
          var response = $.parseJSON(jqXHR.responseText);
          //alert(response.description);
        },
        success: function() {
          _this.removeAttr("disabled", "disabled");
          _this.removeAttr("aria-busy", "true");
          _this.closest('.custom-variant-wrap').append(`<input type="hidden" class="sub-product_meta-property" name="properties[_personalize]" value="main"><input type="hidden" class="sub-product_meta-property" name="properties[_bundleid]" value="${randomId}">`);
          // document.getElementById('mini-cart').open = true;
          document.querySelector('.cart-notification').innerHTML = `<div class="cart-notification__overflow">
            <div class="container">
              <div class="cart-notification__wrapper">
                <svg focusable="false" width="20" height="20" class="icon icon--cart-notification" viewBox="0 0 20 20">
                  <rect width="20" height="20" rx="10" fill="currentColor"></rect>
                  <path d="M6 10L9 13L14 7" fill="none" stroke="rgb(var(--success-color))" stroke-width="2"></path>
                </svg>
                
                <div class="cart-notification__text-wrapper">
                  <span class="cart-notification__heading heading hidden-phone">Product added to your cart!</span>
                  <span class="cart-notification__heading heading hidden-tablet-and-up">Product Added to your cart!</span>
                  <a href="/cart" class="cart-notification__view-cart link" data-no-instant>View cart</a>
                </div>
              </div>
            </div>
          </div>`
          document.querySelector('.cart-notification').removeAttribute('hidden');
          setTimeout(() => {
            document.querySelector('.cart-notification').setAttribute('hidden', 'true');
          }, 3000);        
          
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));
          // window.location.href = '/cart';
        }
      });
    // console.log('product',items,items[0].properties._bundleid);
  });

  $('body').on('click','#product-meta-collapsible__item-scroll', function(){
    var headerHeight = $(".header").outerHeight();
    $("html, body").animate({
      scrollTop: $(".multi-image-with-text-sec").offset().top - headerHeight - 20
    }, 800); 
  })

  $('body').on('click','.custom-variant-atc-button-popup', function(){
    $('html').addClass('lock-all');
    $('.mini_order-popup').addClass('show');
  });
  $('body').on('click','.mini_order-close-popup', function(){
    $('html').removeClass('lock-all');
    $('.mini_order-popup').removeClass('show');
  });
  $(".mini_order-popup").click(function(event) {
      if ($(event.target).is(".mini_order-popup")) {
        $('html').removeClass('lock-all');
          $(".mini_order-popup").removeClass('show');
      }
  });

  // $('body').on('click','.floating-call--icon', function(e){
  //   e.preventDefault();
  //   $(this).find('.header-call-number').toggleClass('active');
  // })

  $('body').on('click','.line-item__remove-button', function(){
    $(this).closest('.line-item__remove-list-container, .line-item__product').find('.line-item-loader').css('display', 'block');
  })

  $(".show_pw_icon").click(function() {
    $(this).toggleClass("show");
    var input = $('.pw_input');
    if (input.attr("type") == "password") {
      input.attr("type", "text");
    } else {
      input.attr("type", "password");
    }
  });

   $(".product-meta__product-offer_link").click(function () {
    var target = $(".product-meta-offer-condition");
    var targetOffset = target.offset().top;
    var windowHeight = $(window).height();
    var scrollPosition = targetOffset - (windowHeight / 2) + (target.outerHeight() / 2);

    $("html, body").animate({ scrollTop: scrollPosition }, "slow");
  });
  
  
});

var elementHeight = $('.product-meta-collapsible__item').outerWidth();
document.documentElement.style.setProperty('--dynamic-width', elementHeight + 'px');
// console.log('elementHeight',elementHeight)
